import { ReactNode, useEffect, useRef, useState } from "react";

interface Tab {
    id?: number;
    label: string;
    icon?: ReactNode;
    width?: string;
}

interface TabsProps {
    tabs: Tab[];
    onChange?: (index: number, tab?: Tab) => void;
    activeIndex: number;
    width?: string;
}

export const Tabs = ({ tabs, onChange, activeIndex, width = "max-w-full" }: TabsProps) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const overflowBtnRef = useRef<HTMLButtonElement | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const timerRef = useRef<number | null>(null);
    const lastKeyRef = useRef<string>(""); // remember last overflow key to avoid redundant updates

    const [overflowIndices, setOverflowIndices] = useState<number[]>([]);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current?.contains(event.target as Node) &&
                overflowBtnRef.current &&
                !overflowBtnRef.current?.contains(event.target as Node)
            ) {
                setShowDropdown(false);
            }
        };

        if (showDropdown) {
            document.addEventListener("click", handleClickOutside);
        } else {
            document.removeEventListener("click", handleClickOutside);
        }

        return () => document.removeEventListener("click", handleClickOutside);
    }, [showDropdown]);

    const handleTabClick = (index: number, tab: Tab) => {
        onChange?.(index, tab);
    };

    const checkOverflow = () => {
        if (showDropdown) setShowDropdown(false);
        const container = containerRef.current;
        if (!container) return;

        const availableWidth = container?.clientWidth;
        if (!availableWidth) return;
        const reservedForHamburger = 80;

        let used = 0;
        const newOverflowIdx: number[] = [];

        for (let i = 0; i < tabs?.length; i++) {
            const el = tabRefs?.current[i];
            if (!el) break;
            used += el?.offsetWidth;
            if (used > (availableWidth - reservedForHamburger)) {
                newOverflowIdx.push(i);
            }
        }

        const key = newOverflowIdx.join(",");
        if (lastKeyRef.current !== key) {
            lastKeyRef.current = key;
            setOverflowIndices(newOverflowIdx);
            setIsOverflowing(newOverflowIdx?.length > 0);
        }
    };

    // Single scheduler used both for initial check and ResizeObserver callbacks.
    const scheduleCheck = () => {
        if (timerRef.current) {
            window.clearTimeout(timerRef.current);
        }
        timerRef.current = window.setTimeout(() => {
            // run just after paint
            requestAnimationFrame(() => {
                checkOverflow();
            });
        });
    };

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const ro = new ResizeObserver(() => {
            scheduleCheck();
        });
        ro.observe(container);

        scheduleCheck(); // initial run

        return () => {
            ro.disconnect();
            if (timerRef.current) cancelAnimationFrame(timerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabs.length, activeIndex]);



    return (
        <div className="w-full relative">
            {/* Tabs Container */}
            <div ref={containerRef} className="w-full">
                <div className="flex bg-gray-100 px-[3px] py-[4px] rounded-lg gap-1.5 w-max overflow-hidden transition-all duration-300 ease-in-out border border-primary">
                    {tabs?.map((tab, i) => {
                        const isActive = tab?.id ? tab?.id === activeIndex : i === activeIndex;
                        const isOverflowed = overflowIndices?.includes(i);

                        return (
                            <button
                                key={tab?.id ?? i}
                                ref={(el) => {
                                    tabRefs.current[i] = el;
                                }}
                                onClick={() => handleTabClick(i, tab)}
                                className={`flex items-center gap-2 px-[8px] py-[8px] rounded-md transition-all whitespace-nowrap 
              ${isActive ? "bg-primary text-white shadow-md" : "text-gray-600"} 
              ${isOverflowed ? "absolute opacity-0 pointer-events-none" : ""}`}
                            >
                                {tab?.icon && (
                                    <span
                                        className={`material-symbols-outlined text-[25px] 
                  ${isActive ? "text-white" : "text-gray-600"}`}
                                    >
                                        {tab?.icon}
                                    </span>
                                )}

                                <span
                                    className={`text-[12px] font-medium 
                ${isActive ? "text-white" : "text-gray-600"}`}
                                >
                                    {tab?.label}
                                </span>
                            </button>
                        );
                    })}

                    {/* Overflow Menu Button */}
                    {isOverflowing && (
                        <button
                            ref={overflowBtnRef}
                            onClick={() => setShowDropdown(!showDropdown)}
                            className="flex items-center justify-between py-[5px] px-[6px] bg-white gap-1.5 rounded-lg border border-primary"
                        >
                            <span className="material-symbols-outlined text-[18px] font-light text-gray-700">
                                menu
                            </span>
                        </button>
                    )}
                </div>
            </div>

            {/* Dropdown */}
            {showDropdown && (
                <div
                    ref={dropdownRef}
                    className="bg-white absolute right-[20px] mt-2 rounded-lg shadow-lg border border-primary z-[999]"
                >
                    <div className="flex flex-col min-w-[150px] max-h-[300px] overflow-y-auto">
                        {overflowIndices?.map((idx) => {
                            const tab = tabs[idx];
                            const isActive = tab?.id ? tab?.id === activeIndex : idx === activeIndex;

                            return (
                                <button
                                    key={tab?.id ?? idx}
                                    onClick={() => handleTabClick(idx, tab)}
                                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-left 
                ${isActive ? "bg-primary text-white" : "hover:bg-gray-100 text-gray-600"}`}
                                >
                                    {tab?.icon && (
                                        <span
                                            className={`material-symbols-outlined 
                    ${isActive ? "text-white" : "text-gray-600"}`}
                                        >
                                            {tab?.icon}
                                        </span>
                                    )}

                                    <span
                                        className={`text-sm 
                  ${isActive ? "text-white" : "text-gray-600"}`}
                                    >
                                        {tab.label}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>

    );
};
